/* HydroHub inside the proposal's browser frames. The system shown is the real portal: the portal's own components
   (charts, tables, shell) rendered with the example dataset and laid out for three widths – full (1360×820, with
   sidebar), compact (1040×640) and phone (390 wide) – so the Stage can scale it 1:1 and it stays legible on every
   screen. Nothing here is an image.
   HeroShot – Oversikt in the hero · SystemShots – the six views with tabs, plays by itself, a click takes over ·
   WorkspaceShot – under the AI section: a question in «Spør HydroHub», then the AI workspace. */
import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, ChartColumn, Gauge, MessageSquare, Waves, Wrench } from "lucide-react";
import { Inert, Stage } from "../Stage";
import { HhView } from "../hh";

const ICONS = { oversikt: Gauge, kraftverk: Waves, hendelser: Bell, rapporter: ChartColumn, spor: MessageSquare, service: Wrench, ai: MessageSquare };
const VIEW_MS = 7000;
const MANUAL_MS = 30000;

/* one view of the portal, laid out for `mode` */
export const SkView = ({ k, mode = "full" }) => (
  <div className="h-full w-full" data-testid={`sk-view-${k}`} data-mode={mode}>
    <HhView k={k} mode={mode} />
  </div>
);

export const HeroShot = ({ d }) => {
  const k = d.shots.heroKey || "oversikt";
  return (
    <Stage url={`${d.shots.host}${d.shots.heroUrl || "/"}`} caption={d.ui.prototype} height={820} narrowHeight={760} widths={{ fullWidth: 1360 }} testId="tilbod-hero-still" attrs={{ "data-view": k }}>
      {({ mode }) => (
        <Inert className="h-full w-full">
          <SkView k={k} mode={mode} />
        </Inert>
      )}
    </Stage>
  );
};

/* one view at a time while in view; a click on a tab takes over for a while, then it plays on */
const useScript = (n, active) => {
  const [st, setSt] = useState({ v: 0, loop: 0 });
  const [paused, setPaused] = useState(false);
  const manualUntil = useRef(0);
  useEffect(() => {
    if (!active || paused || n < 2) return undefined;
    const t = setTimeout(() => setSt((s) => (manualUntil.current > Date.now() ? { v: s.v, loop: s.loop + 1 } : { v: (s.v + 1) % n, loop: s.loop + 1 })), VIEW_MS);
    return () => clearTimeout(t);
  }, [st, active, paused, n]);
  const select = useCallback((i) => {
    manualUntil.current = Date.now() + MANUAL_MS;
    setSt((s) => ({ v: i, loop: s.loop + 1 }));
  }, []);
  return { ...st, paused, setPaused, select };
};

const Tabs = ({ views, current, loop, paused, onSelect }) => {
  const refs = useRef([]);
  const strip = useRef(null);
  useEffect(() => {
    const el = refs.current[current];
    const s = strip.current;
    if (!el || !s || s.scrollWidth <= s.clientWidth + 1) return;
    const left = el.offsetLeft - (s.clientWidth - el.offsetWidth) / 2;
    s.scrollTo({ left: Math.max(0, Math.min(left, s.scrollWidth - s.clientWidth)), behavior: "smooth" });
  }, [current]);
  const onKey = (e, i) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const n = (i + (e.key === "ArrowRight" ? 1 : -1) + views.length) % views.length;
    onSelect(n);
    refs.current[n]?.focus();
  };
  return (
    <div ref={strip} className="kvl-switch" role="tablist" aria-label="Visning" data-paused={paused ? "true" : "false"} data-testid="tilbod-system-tabs">
      {views.map((v, i) => {
        const Icon = ICONS[v.key] || Gauge;
        const on = i === current;
        return (
          <button key={v.key} ref={(el) => { refs.current[i] = el; }} type="button" role="tab" aria-selected={on} tabIndex={on ? 0 : -1} className="kvl-switch-tab" onClick={() => onSelect(i)} onKeyDown={(e) => onKey(e, i)} data-testid={`tilbod-system-tab-${v.key}`}>
            <span className="kvl-switch-ic"><Icon aria-hidden="true" /></span>
            <span className="kvl-switch-txt"><b>{v.label}</b>{v.hint && <small>{v.hint}</small>}</span>
            {on && <i key={`${v.key}-${loop}`} className="kvl-switch-bar" style={{ "--dur": `${VIEW_MS}ms` }} aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
};

export const SystemShots = ({ d }) => {
  const views = d.idea.views;
  const [active, setActive] = useState(false);
  const { v, loop, paused, setPaused, select } = useScript(views.length, active);
  const cur = views[v];
  return (
    <figure className="kvl-figure kvl-figure-mock" data-testid="tilbod-idea-figure" data-view={cur.key}>
      <Tabs views={views} current={v} loop={loop} paused={paused || !active} onSelect={select} />
      <div className="kvl-frame" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <Stage url={`${d.shots.host}${cur.url}`} caption={d.ui.prototype} height={820} narrowHeight={640} widths={{ fullWidth: 1360 }} testId="tilbod-system-mockup" attrs={{ "data-view": cur.key }} onActive={setActive}>
          {({ mode }) => (
            <Inert className="h-full w-full">
              <div key={cur.key} className="mb-view-enter h-full w-full" data-testid={`tilbod-system-view-${cur.key}`}>
                <SkView k={cur.key} mode={mode} />
              </div>
            </Inert>
          )}
        </Stage>
      </div>
      <figcaption key={cur.key} className="kvl-cap-enter" data-testid="tilbod-idea-caption">{cur.caption}</figcaption>
    </figure>
  );
};

/* under the AI section: a question asked in «Spør HydroHub» on the overview, then the AI workspace with the answer */
export const WorkspaceShot = ({ d }) => {
  const ex = d.ai.example;
  const pair = ex.views || [{ key: "spor", url: "/" }, { key: "ai", url: "/ai" }];
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const t = setTimeout(() => setI((x) => (x + 1) % pair.length), 6500);
    return () => clearTimeout(t);
  }, [i, active, pair.length]);
  const cur = pair[i];
  return (
    <Stage url={`${d.shots.host}${cur.url}`} caption={d.ui.prototype} height={820} narrowHeight={640} widths={{ fullWidth: 1360 }} testId="tilbod-workspace-mockup" attrs={{ "data-view": cur.key }} onActive={setActive}>
      {({ mode }) => (
        <Inert className="h-full w-full">
          <div key={cur.key} className="mb-view-enter h-full w-full" data-testid={`tilbod-workspace-view-${cur.key}`}>
            <SkView k={cur.key} mode={mode} />
          </div>
        </Inert>
      )}
    </Stage>
  );
};
